// Справка „Разнесени отчети": KPI (само активните), сегменти Разнесени /
// Анулирани / Всички с фасетни броячи (пряк път към филтъра „Статус"; отваря
// се на „Разнесени", „Изчисти" → „Всички"),
// търсачка, филтри В ЗАГЛАВИЯТА НА КОЛОНИТЕ (Разнесъл · Статус) с ред
// „Филтри: …" над таблицата, Excel и таблицата. Клик на ред отваря документа;
// „Печат" и менюто „⋯" са на реда. „Коригирай и разнеси повторно" чете
// документа ПРЕДИ анулирането (замените му) и отваря отчета наново в
// „Разнасяне на отчети" с тях.
import { useEffect, useMemo, useState } from "react";
import { App, ConfigProvider } from "antd";
import {
  AppstoreOutlined,
  EuroOutlined,
  FileSearchOutlined,
  ProfileOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { useIsDarkTheme } from "../../../../store/hooks";
import {
  useAnnulReportPosting,
  usePostedReports,
} from "../../../../hooks/useInventoryPostings";
import { getReportPosting, type ReportPosting } from "../../../../api/inventory";
import { exportToExcel } from "../../../../utils/excelExport";
import { PageHeader } from "../../components/list/PageHeader";
import {
  EmptyScreen,
  KpiGrid,
  SearchBox,
  SectionCard,
  SegmentedCounts,
} from "../../components/list/ListBits";
import { ReportToolbar } from "../../components/list/ReportToolbar";
import { refreshOf } from "../../components/list/reportRefresh";
import {
  periodOfPreset,
  periodToParams,
  type Period,
} from "../../components/period/periodPresets";
import { FilterSummaryLine } from "../../components/filters/FilterSummaryLine";
import { allSections, useColumnFilters, type ColumnSections } from "../../components/filters/useColumnFilters";
import { useShowMore } from "../../components/list/useShowMore";
import { formatMoney } from "../../components/numbers";
import { fmtDateTime } from "../../components/dateTime";
import { AnnulDocumentModal } from "../../components/AnnulDocumentModal";
import { useActionToast } from "../../components/useActionToast";
import { ReportPostingDocumentModal } from "../../posting/components/ReportPostingDocumentModal";
import { isPostingAnnulled } from "../../posting/components/reportPostingShared";
import type { RowAction } from "../../components/list/RowActionsMenu";
import { ReportLoading, ReportNoMatch, ShowMoreRow } from "../components/ReportListBits";
import {
  EMPTY_POSTING_FILTERS,
  INITIAL_POSTING_FILTERS,
  hasAnnulledPostings,
  postingFilterSpecs,
  postingSegmentOf,
  postingTotals,
  postingUserOf,
  searchPostings,
  type PostingFilterKey,
  type PostingStatusKey,
} from "./postedReportsModel";
import { PostedReportsTable, type TableDensity } from "./PostedReportsTable";

export const PostedReportsReport = () => {
  const { t } = useTranslation();
  const isDark = useIsDarkTheme();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const { isNarrow, isWide } = useViewport();
  const showToast = useActionToast();
  const annulMutation = useAnnulReportPosting();

  const [period, setPeriod] = useState<Period>(() => periodOfPreset("last7"));
  const [query, setQuery] = useState("");
  const postingsQuery = usePostedReports({
    ...periodToParams(period),
    status: "all",
  });
  const { data: postings = [], isLoading } = postingsQuery;
  const { refresh, refreshing } = refreshOf(postingsQuery);

  // ─── Документният модал и действията ─────────────────────────────────────
  const [doc, setDoc] = useState<{ posting: ReportPosting; autoPrint: boolean } | null>(null);
  const [annul, setAnnul] = useState<{ posting: ReportPosting; repost: boolean } | null>(null);
  const [flashId, setFlashId] = useState<number | null>(null);

  // След разнасяне: „Виж документа" идва със state.openDoc → мигащ ред + модал
  useEffect(() => {
    const state = location.state as { openDoc?: ReportPosting } | null;
    if (!state?.openDoc) return;
    setFlashId(state.openDoc.id);
    setDoc({ posting: state.openDoc, autoPrint: false });
    navigate(location.pathname, { replace: true, state: null });
    const timer = setTimeout(() => setFlashId(null), 1600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const confirmAnnul = async (reason: string | null) => {
    if (!annul) return;
    const { posting, repost } = annul;
    try {
      // Корекция: замените на документа се четат ПРЕДИ анулирането
      const saved = repost ? await getReportPosting(posting.id) : null;
      await annulMutation.mutateAsync({ id: posting.id, reason });
      setAnnul(null);
      setDoc(null);
      if (repost && posting.reportId != null) {
        navigate("/inventory/posting", {
          state: {
            selectReportId: posting.reportId,
            prefillSubstitutions: saved?.savedSubstitutions.map((sub) => ({
              warehouseId: sub.warehouseId,
              recipientProductId: sub.recipientProductId,
              donorProductId: sub.donorProductId,
              quantity: sub.quantity,
            })),
          },
        });
        return;
      }
      showToast({ text: t("inventoryPage.annulledToast", { number: posting.id }) });
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } };
      message.error(err.response?.data?.detail || t("inventoryPage.errDelete"));
    }
  };

  const actionsOf = (posting: ReportPosting): RowAction[] => {
    const base: RowAction[] = [
      { key: "print", label: t("inventoryPage.print"), onClick: () => setDoc({ posting, autoPrint: true }) },
      { key: "view", label: t("inventoryPage.viewDocument"), onClick: () => setDoc({ posting, autoPrint: false }) },
    ];
    if (isPostingAnnulled(posting)) return base;
    return [
      ...base,
      {
        key: "repost",
        label: t("inventoryPage.postingRepost"),
        hint: t("inventoryPage.postingRepostHint"),
        dividerBefore: true,
        onClick: () => setAnnul({ posting, repost: true }),
      },
      {
        key: "annul",
        label: t("inventoryPage.docDelete"),
        danger: true,
        onClick: () => setAnnul({ posting, repost: false }),
      },
    ];
  };

  // ─── Филтри в колоните (броячите — фасетни), KPI за периода ─────────────
  const specs = useMemo(
    () =>
      postingFilterSpecs({
        titles: { users: t("inventoryPage.filterPostedBy"), statuses: t("inventoryPage.colStatus") },
        statusLabel: (value) =>
          value === "annulled" ? t("inventoryPage.segAnnulled") : t("inventoryPage.segPosted"),
      }),
    [t],
  );
  const searched = useMemo(() => searchPostings(postings, query), [postings, query]);
  // Отваря се на „Разнесени"; „Изчисти" → празното („Всички")
  const filters = useColumnFilters({
    specs,
    empty: EMPTY_POSTING_FILTERS,
    initial: INITIAL_POSTING_FILTERS,
    universe: postings,
    base: searched,
  });
  const shown = filters.rows;
  const { visible, hiddenCount, showMore } = useShowMore(shown, [period, filters.state, query]);
  const clearAll = () => {
    filters.clearAll();
    setQuery("");
  };
  const sections: ColumnSections<PostingFilterKey> = {
    users: filters.section("users"),
    statuses: filters.section("statuses"),
  };
  // Сегментите = филтърът „Статус" (броячите — фасетни, както в попъпа)
  const statusCounts = new Map(filters.facet("statuses").map((option) => [option.value, option.count]));
  const onStatusSegment = (key: PostingStatusKey) =>
    filters.setValue("statuses", key === "all" ? [] : [key]);

  const periodTotals = postingTotals(postings);
  const totals = postingTotals(shown);
  const kpis = [
    { label: t("inventoryPage.kpiPostings"), value: String(periodTotals.count), Icon: ProfileOutlined },
    {
      label: t("inventoryPage.kpiPostedProducts"),
      value: String(periodTotals.products),
      Icon: AppstoreOutlined,
    },
    {
      label: t("inventoryPage.colSubstitutions"),
      value: String(periodTotals.substitutions),
      Icon: SwapOutlined,
    },
    {
      label: t("inventoryPage.kpiValueGross"),
      value: formatMoney(periodTotals.gross),
      Icon: EuroOutlined,
      tone: "brand" as const,
    },
  ];

  const exportAll = () =>
    exportToExcel({
      filename: "razneseni-otcheti",
      sheetName: t("inventoryPage.navPostedReports"),
      rows: shown,
      columns: [
        { header: t("inventoryPage.colDocument"), value: (p) => p.id },
        { header: t("inventoryPage.colReport"), value: (p) => p.reportNumber ?? "" },
        { header: t("inventoryPage.colDateTime"), value: (p) => fmtDateTime(p.createdAt ?? p.date) },
        { header: t("inventoryPage.colUser"), value: (p) => postingUserOf(p) },
        { header: t("inventoryPage.colWrittenOff"), value: (p) => p.productsCount },
        { header: t("inventoryPage.colSubstitutions"), value: (p) => p.substitutionsCount },
        { header: t("inventoryPage.colNet"), value: (p) => p.totalValueNoVat },
        { header: t("inventoryPage.colGross"), value: (p) => p.totalValueWithVat },
        {
          header: t("inventoryPage.colStatus"),
          value: (p) =>
            isPostingAnnulled(p) ? t("inventoryPage.statusAnnulled") : t("inventoryPage.statusPosted"),
        },
        { header: t("inventoryPage.annulReason"), value: (p) => p.deleteReason ?? "" },
      ],
    });

  const density: TableDensity = isNarrow ? "cards" : isWide ? "full" : "medium";

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#D97706",
          colorPrimaryHover: "#B45309",
          controlItemBgActive: isDark ? "#382a12" : "#FFF4E0",
          borderRadius: 8,
        },
      }}
    >
      <div className="flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          kicker={t("inventoryPage.kickerReport")}
          title={t("inventoryPage.navPostedReports")}
          right={
            <ReportToolbar
              period={period}
              onPeriodChange={setPeriod}
              filterSections={allSections(sections)}
              onExport={exportAll}
              exportDisabled={!shown.length}
              onRefresh={refresh}
              refreshing={refreshing}
            />
          }
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto ${
            isNarrow ? "px-3 pb-10 pt-3.5" : "px-7 pb-12 pt-5"
          }`}
        >
          <KpiGrid cards={kpis} />
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedCounts
              value={postingSegmentOf(filters.state.statuses)}
              onChange={onStatusSegment}
              options={[
                { key: "posted", label: t("inventoryPage.segPosted"), count: statusCounts.get("posted") ?? 0 },
                { key: "annulled", label: t("inventoryPage.segAnnulled"), count: statusCounts.get("annulled") ?? 0 },
                { key: "all", label: t("inventoryPage.segAll"), count: filters.facetTotal("statuses") },
              ]}
            />
            <SearchBox
              value={query}
              onChange={setQuery}
              placeholder={t("inventoryPage.postedSearchPh")}
            />
          </div>
          <SectionCard>
            <FilterSummaryLine
              countLabel={t("inventoryPage.woDocsCount", { count: shown.length })}
              items={filters.summary}
              onClearAll={filters.clearAll}
            >
              <span>
                {t("inventoryPage.colGross")}{" "}
                <span className="font-sheet font-bold text-brand-deep-ink">{formatMoney(totals.gross)}</span>
              </span>
            </FilterSummaryLine>
            {isLoading ? (
              <ReportLoading />
            ) : postings.length === 0 ? (
              <EmptyScreen
                Icon={FileSearchOutlined}
                tone="brand"
                title={t("inventoryPage.postedNoDocs")}
              />
            ) : (
              <PostedReportsTable
                rows={visible}
                density={density}
                flashId={flashId}
                filters={sections}
                totalNet={totals.net}
                totalGross={totals.gross}
                totalExcludesAnnulled={hasAnnulledPostings(shown)}
                onOpen={(posting) => setDoc({ posting, autoPrint: false })}
                onPrint={(posting) => setDoc({ posting, autoPrint: true })}
                actionsOf={actionsOf}
                footer={
                  shown.length ? (
                    <ShowMoreRow hiddenCount={hiddenCount} onShowMore={showMore} />
                  ) : (
                    <ReportNoMatch onClear={clearAll} />
                  )
                }
              />
            )}
          </SectionCard>
        </div>

        <ReportPostingDocumentModal
          open={!!doc}
          posting={doc?.posting ?? null}
          autoPrint={doc?.autoPrint}
          onClose={() => setDoc(null)}
          onAnnul={doc ? () => setAnnul({ posting: doc.posting, repost: false }) : undefined}
          onRepost={doc ? () => setAnnul({ posting: doc.posting, repost: true }) : undefined}
        />
        <AnnulDocumentModal
          open={!!annul}
          title={
            annul?.repost
              ? t("inventoryPage.cfmPostingRepostTitle", { report: annul.posting.reportNumber ?? annul.posting.id })
              : t("inventoryPage.cfmPostingAnnulTitle", { report: annul?.posting.reportNumber ?? annul?.posting.id ?? "" })
          }
          text={
            annul?.repost
              ? t("inventoryPage.postingRepostText", { report: annul.posting.reportNumber ?? "" })
              : t("inventoryPage.postingAnnulText", { report: annul?.posting.reportNumber ?? "" })
          }
          confirmLabel={
            annul?.repost ? t("inventoryPage.cfmPostingRepostConfirm") : t("inventoryPage.cfmPostingAnnulConfirm")
          }
          note={{ kind: "undo", text: t("inventoryPage.cfmPostingAnnulNote") }}
          saving={annulMutation.isPending}
          onCancel={() => setAnnul(null)}
          onConfirm={confirmAnnul}
        />
      </div>
    </ConfigProvider>
  );
};

export default PostedReportsReport;
